import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';

import NumberInput, { SelectedNumpadTab } from '~ComponentsImproved/Numpad';
import { useStaffCan, useI18n, useButton } from '~/Hooks';
import { DISCOUNT_TYPE, OFFER_TYPE } from '~../__queries__';
import { BlueButton, WhiteButton } from '~Styles';
import ListItem, { ListItemWrapper } from '~Components/DiscountButton/ListItem';
import { useCurrency } from '~Hooks/useCurrency';
import { useCart } from '~Components/CartProvider';
import { MEMBERSHIP_DISCOUNT_ID } from '~Components/LinkUser/consts';
import useOffers from '~Hooks/useOffers';
import { GetOffers_Company_offers } from '~Hooks/__queries__';
import PromoCodeDiscount from '~Components/PromoCodeDiscount';
import { MANUAL_DISCOUNT_ID } from '~Components/DiscountButton';

const ButtonWrapper = styled.div<{
  fullWidth?: boolean;
}>`
  flex: 1;
  margin: 0 ${(props) => (props.fullWidth ? 0 : 4)}px;
  position: relative;
  min-width: 95%;

  button {
    position: relative;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  height: 100vh;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;
  background-color: rgb(14 18 22 / 75%);
  z-index: 2;
  animation: fade-in 0.2s forwards;
`;

const ListWrapper = styled.div<{ visible: boolean }>`
  position: absolute;
  width: 100%;
  top: -8px;
  transform: translateY(-100%);
  z-index: 3;
  animation: fade-in 0.2s forwards;
  right: 0;
  display: ${({ visible }) => (visible ? 'block' : 'none')};
`;

const Anchor = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 4px;
  background-color: var(--colorWhite);
  max-height: calc(100vh - 440px);
  margin-top: 8px;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const List = styled.div`
  overflow: auto;
`;

const DiscountButton = styled(WhiteButton)`
  font-size: 14px;
  min-height: 56px;
  width: 100%;
  display: block;
`;

interface Props {
  fullWidth?: boolean;
  /**
   * If set, the discount will be applied to the selected items.
   * If not, it applies to the whole cart
   */
  forSelectedItems?: boolean;
  disabled?: boolean;
}

const Discounts = ({ forSelectedItems, fullWidth, disabled }: Props) => {
  const { i18n } = useI18n();
  const cart = useCart();
  const staffCan = useStaffCan();
  const currency = useCurrency();
  const offers = useOffers();

  const [showNumpad, setShowNumpad] = useState<boolean>(false);
  const [showDiscounts, setShowDiscounts] = useState<boolean>(false);

  const offersList = useMemo(
    () =>
      offers.offers.filter((offer) => offer.type === OFFER_TYPE.ORDER_DISCOUNT && offer.enabled),
    [offers.offers],
  );

  const itemIdsForOffer = useMemo<Record<string, string[]>>(() => {
    return offersList.reduce((obj, offer) => {
      const ids = [
        ...(offer.categories
          .map((cat) => cat.items)
          .flat()
          .map((i) => i.id) ?? []),
        ...offer.items.map((i) => i.id),
      ];

      obj[offer.id] = ids;

      return obj;
    }, {} as Record<string, string[]>);
  }, [offersList]);

  const handleAddDiscount = (offer: GetOffers_Company_offers) => {
    if (forSelectedItems) {
      cart.selectedItems.applyDiscount({
        id: offer.id,
        type: offer.discountType,
        value: offer.discountRate,
        title: i18n(offer.nameLang),
      });
      cart.selectedItems.clear();
      return;
    }

    cart.setAppliedDiscount({
      id: offer.id,
      type: offer.discountType,
      value: offer.discountRate,
      itemIds: itemIdsForOffer[offer.id],
      title: i18n(offer.nameLang),
    });
  };

  const handleOfferClick = (offer: GetOffers_Company_offers) => {
    if (offer.id === cart.appliedDiscount?.id) {
      if (forSelectedItems) {
        cart.selectedItems.setDiscount(0);
      } else {
        cart.setAppliedDiscount(null);
      }
    } else {
      handleAddDiscount(offer);
      setShowDiscounts(false);
    }
  };

  const showManualDiscount = () => {
    setShowNumpad(true);
    setShowDiscounts(false);
  };

  const handleManualDiscount = (value: string, tab: SelectedNumpadTab) => {
    if (!tab) {
      return;
    }
    let discountType = DISCOUNT_TYPE.PERCENTAGE;
    if (tab.type === 'freeform') {
      discountType = DISCOUNT_TYPE.PRICE;
    }
    setShowNumpad(false);

    const discount = {
      id: MANUAL_DISCOUNT_ID,
      type: discountType,
      value: discountType == 'PRICE' ? Math.round(+value * 100) : parseInt(value),
    };

    if (forSelectedItems) {
      cart.selectedItems.applyDiscount(discount);
      cart.selectedItems.clear();
    } else {
      cart.setAppliedDiscount(discount);
    }
  };

  const handleDiscountButton = () => {
    //If there are no offers or if the staff is not allowed to use the offers go to manual discount
    if (offersList.length === 0 || !staffCan.PRESET_DISCOUNT) {
      showManualDiscount();
    } else {
      setShowDiscounts(!showDiscounts);
    }
  };

  // Disables the button if there are no offers and the staff cannot do manual discount OR the staff is not allowed too give discounts
  const discountDisabled =
    (offersList.length === 0 && !staffCan.MANUAL_DISCOUNT) ||
    (!staffCan.MANUAL_DISCOUNT && !staffCan.PRESET_DISCOUNT);

  const hasRemoveableDiscount = useMemo(() => {
    if (forSelectedItems) {
      return cart.selectedItems.items.some((item) => item.discount > 0);
    }

    return false;
  }, [cart.selectedItems.items, forSelectedItems]);

  const discountButton = useButton({
    disabled: discountDisabled || disabled,
    onClick: handleDiscountButton,
  });

  const manualDiscount = useButton({
    onClick: showManualDiscount,
  });

  return (
    <>
      <ButtonWrapper
        fullWidth={fullWidth ?? forSelectedItems}
        style={{
          zIndex: showDiscounts ? 3 : 1,
        }}>
        {showDiscounts && <Backdrop onClick={() => setShowDiscounts(false)} />}
        <DiscountButton
          {...discountButton}
          style={{
            zIndex: showDiscounts ? 3 : 1,
            color: showDiscounts ? 'var(--colorUIRed)' : undefined,
          }}>
          {showDiscounts ? i18n('POS.Cancel').toUpperCase() : i18n('POS.Discount').toUpperCase()}
        </DiscountButton>
        <ListWrapper visible={showDiscounts}>
          <Anchor>
            <Container>
              <List>
                {staffCan.MANUAL_DISCOUNT && (
                  <ListItemWrapper fluid>
                    <BlueButton fluid {...manualDiscount}>
                      {i18n('POS.ManualDiscount')}
                    </BlueButton>
                  </ListItemWrapper>
                )}

                {offers.hasActivePromoCodes && (
                  <ListItemWrapper fluid>
                    <PromoCodeDiscount isQSR onClick={() => setShowDiscounts(false)} />
                  </ListItemWrapper>
                )}

                {cart.appliedDiscount &&
                  !offersList.some((offer) => offer.id === cart.appliedDiscount?.id) && (
                    <ListItem
                      name={
                        cart.appliedDiscount.id === MEMBERSHIP_DISCOUNT_ID
                          ? i18n('POS.MembershipDiscount')
                          : cart.appliedDiscount.title ?? i18n('POS.ManualDiscount')
                      }
                      tagText={
                        cart.appliedDiscount.type === DISCOUNT_TYPE.PERCENTAGE
                          ? `${cart.appliedDiscount.value}%`
                          : currency.format(cart.appliedDiscount.value)
                      }
                      tagColor={
                        cart.appliedDiscount.type === DISCOUNT_TYPE.PRICE
                          ? 'var(--colorUIGreen)'
                          : undefined
                      }
                      isActive
                      onClick={() => cart.setAppliedDiscount(null)}
                    />
                  )}

                {offersList.map((offer) => {
                  const isActive = cart.appliedDiscount?.id === offer.id;

                  return (
                    <ListItem
                      key={offer.id}
                      name={i18n(offer.nameLang)}
                      tagText={
                        offer.discountType === DISCOUNT_TYPE.PERCENTAGE
                          ? `${offer.discountRate}%`
                          : currency.format(offer.discountRate)
                      }
                      tagColor={
                        offer.discountType === DISCOUNT_TYPE.PRICE
                          ? 'var(--colorUIGreen)'
                          : undefined
                      }
                      isActive={isActive}
                      onClick={() => handleOfferClick(offer)}
                    />
                  );
                })}

                {hasRemoveableDiscount && (
                  <ListItem
                    key={'remove'}
                    name={i18n('POS.Clear')}
                    isActive
                    onClick={() => {
                      if (forSelectedItems) {
                        cart.selectedItems.setDiscount(0);
                      } else {
                        cart.setAppliedDiscount(null);
                      }
                    }}
                  />
                )}
              </List>
            </Container>
          </Anchor>
        </ListWrapper>
      </ButtonWrapper>

      {showNumpad && (
        <NumberInput
          onSubmit={handleManualDiscount}
          tabs={[
            { title: i18n('POS.Percentage'), type: 'percentage' },
            { title: i18n('POS.Amount'), type: 'freeform' },
          ]}
          maxValue={cart.total / 100}
          onClose={() => setShowNumpad(false)}
          decimal
        />
      )}
    </>
  );
};

export default React.memo(Discounts);
